/* BlairTLV — infinite scroll for the collection + search grids (Claude 2026-10-18)
 *
 * Founder: "change my pages to infinite scroll instead of pagination, but it's important that I can
 * return to the same spot after I click back from a product page ... don't restart the list each
 * time".
 *
 * Markup: snippets/blair-infinite-scroll.liquid renders <div data-blair-infinite> right after the
 * product <ul>, with the current page and the server's own next/previous page URLs (those already
 * carry the active filters + sort, so nothing here rebuilds a query string).
 *
 * LOAD     an IntersectionObserver on that element fetches the next page through Section Rendering
 *          (?page=N&section_id=<id>) 1200px before the shopper reaches the end, appends its cards
 *          (deduped by product URL), and history.replaceState()s ?page=N so a refresh is sane.
 * SAVE     on pagehide (and on a click into a card) the list's HTML, the loaded page range and an
 *          ANCHOR (the card at the top of the viewport and its offset) go into sessionStorage, keyed
 *          by the URL minus page.
 * RESTORE  done entirely by the inline script in the snippet, at parse time, so it lands before
 *          first paint, then re-placed every frame until load.
 *
 * Why an anchor card and not scrollY: on phones the cards past the 8th use content-visibility:auto
 * with a 239px size estimate (main-collection-product-grid.liquid). In a fresh document the cards
 * above the viewport have never rendered, so their height is the estimate, and a raw scrollY lands
 * somewhere else on any phone that is not 390px wide. Placing the same card at the same viewport
 * offset is right whatever the estimates are.
 *
 * bfcache: if the page comes back from the back/forward cache everything is already where it was;
 * nothing here runs again.
 *
 * Filters / sort: facets.js replaces #ProductGridContainer's innerHTML. A MutationObserver on it
 * tears the old instance down and starts a fresh one on the new (page 1) markup.
 */
(function () {
  'use strict';
  if (!('IntersectionObserver' in window) || !window.fetch) return;

  var PREFIX = 'blair-inf:';
  var MAX_AGE = 3600000;
  var active = null;

  function listKey(href) {
    var u = new URL(href, location.origin);
    u.searchParams.delete('page');
    u.searchParams.delete('section_id');
    // Shopify's own tracking params (e.g. _su_rec, added when Shop's "second visit" redirect runs on
    // the way BACK to the list, or _pos/_sid/_ss from search) must not change the key, or the saved
    // list is never found and the shopper lands on page N at the top. The inline restore script in
    // snippets/blair-infinite-scroll.liquid strips the same params.
    Array.from(u.searchParams.keys()).forEach(function (k) { if (k.charAt(0) === '_') u.searchParams.delete(k); });
    u.hash = '';
    if (u.searchParams.sort) u.searchParams.sort();
    return PREFIX + u.pathname + '?' + u.searchParams.toString();
  }

  function cardKey(li) {
    var a = li.querySelector('a[href*="/products/"]');
    if (!a) return null;
    try { return new URL(a.getAttribute('href'), location.origin).pathname; } catch (e) { return null; }
  }

  function animateIn(items) {
    // animations-cascade.js: the batch index becomes --animation-order, so a fresh page cascades
    // from 0 instead of inheriting a 37th-card delay.
    if (typeof initializeScrollAnimationTrigger !== 'function' || !items.length) return;
    initializeScrollAnimationTrigger({ getElementsByClassName: function () { return items; } });
  }

  function Grid(root) {
    this.root = root;
    this.scope = root.parentNode;
    this.list = this.scope.querySelector('ul.product-grid');
    this.sectionId = root.getAttribute('data-section-id');
    this.page = parseInt(root.getAttribute('data-page'), 10) || 1;
    this.first = parseInt(root.getAttribute('data-first') || root.getAttribute('data-page'), 10) || 1;
    this.next = root.getAttribute('data-next') || '';
    this.prev = root.getAttribute('data-prev') || '';
    this.loading = false;
    this.seen = new Set();
    this.dead = false;
    if (!this.list) return;
    var self = this;
    Array.prototype.forEach.call(this.list.children, function (li) {
      var k = cardKey(li);
      if (k) self.seen.add(k);
    });

    this.io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) if (entries[i].isIntersecting) self.loadNext();
    }, { rootMargin: '0px 0px 1200px 0px' });
    if (this.next) this.io.observe(root);
    else root.classList.add('is-end');

    var more = root.querySelector('.blair-infinite__more');
    if (more) more.addEventListener('click', function (e) { e.preventDefault(); root.classList.remove('is-error'); self.loadNext(); });

    if (this.first > 1 && this.prev) this.addPrevButton();
  }

  Grid.prototype.fetchPage = function (href) {
    var u = new URL(href, location.origin);
    u.searchParams.set('section_id', this.sectionId);
    return fetch(u.toString(), { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var root = doc.querySelector('[data-blair-infinite]');
      var list = doc.querySelector('ul.product-grid');
      return {
        items: list ? Array.prototype.slice.call(list.children) : [],
        page: root ? parseInt(root.getAttribute('data-page'), 10) : null,
        next: root ? root.getAttribute('data-next') || '' : '',
        prev: root ? root.getAttribute('data-prev') || '' : ''
      };
    });
  };

  Grid.prototype.fresh = function (items) {
    var self = this, out = [];
    items.forEach(function (li) {
      var k = cardKey(li);
      if (k && self.seen.has(k)) return;
      if (k) self.seen.add(k);
      li.classList.remove('scroll-trigger--offscreen');
      out.push(document.importNode(li, true));
    });
    return out;
  };

  Grid.prototype.loadNext = function () {
    if (this.loading || !this.next || this.dead) return;
    var self = this;
    this.loading = true;
    this.root.classList.add('is-loading');
    this.fetchPage(this.next).then(function (res) {
      // Blair (2026-10-18, perf): parsing the fetched page and inserting + laying out its cards used
      // to be ONE task (~1s on a throttled phone, in the middle of the shopper's scroll). Yield once
      // between the two so the browser can paint scroll frames in between.
      return new Promise(function (resolve) { setTimeout(function () { resolve(res); }, 0); });
    }).then(function (res) {
      if (self.dead) return;
      var add = self.fresh(res.items);
      var frag = document.createDocumentFragment();
      add.forEach(function (li) { frag.appendChild(li); });
      self.list.appendChild(frag);
      animateIn(add);
      if (res.page) self.page = res.page;
      self.next = res.next;
      self.updateUrl();
      self.loading = false;
      self.root.classList.remove('is-loading');
      if (!self.next) {
        self.io.disconnect();
        self.root.classList.add('is-end');
      } else {
        // Still inside the 1200px margin (short page, fast scroller)? Re-observing makes the
        // observer report the current state again, which chains the next load.
        self.io.unobserve(self.root);
        self.io.observe(self.root);
      }
    }).catch(function (err) {
      if (self.dead) return;
      self.loading = false;
      self.root.classList.remove('is-loading');
      self.root.classList.add('is-error');
      if (window.console) console.warn('[blair-infinite] page load failed', err);
    });
  };

  Grid.prototype.addPrevButton = function () {
    var self = this;
    var he = (document.documentElement.lang || '').indexOf('he') === 0;
    var wrap = document.createElement('div');
    wrap.className = 'blair-infinite__prev';
    var a = document.createElement('a');
    a.className = 'button button--secondary';
    a.href = this.prev;
    a.textContent = he ? 'הצג מוצרים קודמים' : 'Show previous products';
    wrap.appendChild(a);
    this.list.parentNode.insertBefore(wrap, this.list);
    this.prevWrap = wrap;
    a.addEventListener('click', function (e) {
      e.preventDefault();
      if (self.loading) return;
      self.loading = true;
      a.setAttribute('aria-busy', 'true');
      self.fetchPage(self.prev).then(function (res) {
        if (self.dead) return;
        var add = self.fresh(res.items);
        // Keep the first card currently on screen exactly where it is while the new page goes in
        // above it.
        var ref = self.list.firstElementChild;
        var before = ref ? ref.getBoundingClientRect().top : 0;
        var frag = document.createDocumentFragment();
        add.forEach(function (li) { li.classList.add('scroll-trigger--cancel'); frag.appendChild(li); });
        self.list.insertBefore(frag, ref);
        if (ref) window.scrollBy(0, ref.getBoundingClientRect().top - before);
        if (res.page) self.first = res.page;
        self.prev = res.prev;
        self.loading = false;
        a.removeAttribute('aria-busy');
        if (!self.prev || self.first <= 1) { wrap.remove(); self.prevWrap = null; }
        else a.href = self.prev;
      }).catch(function () { self.loading = false; a.removeAttribute('aria-busy'); location.href = a.href; });
    });
  };

  Grid.prototype.updateUrl = function () {
    try {
      var u = new URL(location.href);
      if (this.page > 1) u.searchParams.set('page', this.page);
      else u.searchParams.delete('page');
      // Keep facets.js's history.state ({searchParams}) untouched; only the visible URL changes.
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    } catch (e) {}
  };

  Grid.prototype.anchor = function (li) {
    var items = this.list.children;
    if (!items.length) return { idx: 0, top: 0 };
    if (!li) {
      // First card whose bottom is below the top of the viewport. Binary search: the grid is in
      // document order, so this is ~6 rect reads even with 200 cards.
      var lo = 0, hi = items.length - 1;
      while (lo < hi) {
        var mid = (lo + hi) >> 1;
        if (items[mid].getBoundingClientRect().bottom > 0) hi = mid; else lo = mid + 1;
      }
      li = items[lo];
    }
    return { idx: Array.prototype.indexOf.call(items, li), top: li.getBoundingClientRect().top };
  };

  Grid.prototype.save = function (li) {
    if (this.dead || !this.list || !this.list.isConnected) return;
    var key = listKey(location.href);
    var a = this.anchor(li);
    var st = {
      v: 1, t: Date.now(), html: this.list.innerHTML,
      first: this.first, last: this.page, next: this.next, prev: this.first > 1 ? this.prev : '',
      anchorIdx: a.idx, anchorTop: a.top, y: window.pageYOffset
    };
    try {
      // One saved list per tab: drop other collections' states so the quota never fills up.
      for (var i = sessionStorage.length - 1; i >= 0; i--) {
        var k = sessionStorage.key(i);
        if (k && k.indexOf(PREFIX) === 0 && k !== key) sessionStorage.removeItem(k);
      }
      sessionStorage.setItem(key, JSON.stringify(st));
    } catch (e) {
      try { sessionStorage.removeItem(key); } catch (e2) {}
    }
  };

  Grid.prototype.destroy = function () {
    this.dead = true;
    if (this.io) this.io.disconnect();
  };

  function boot() {
    var container = document.getElementById('ProductGridContainer');
    var root = container && container.querySelector('[data-blair-infinite].blair-infinite--on');
    if (active) active.destroy();
    active = root ? new Grid(root) : null;
  }

  function start() {
    boot();

    var container = document.getElementById('ProductGridContainer');
    if (container) {
      new MutationObserver(function () {
        var root = container.querySelector('[data-blair-infinite]');
        if (active && active.root === root) return;
        if (root && !root.classList.contains('blair-infinite--on')) root.classList.add('blair-infinite--on');
        boot();
      }).observe(container, { childList: true });

      container.addEventListener('click', function (e) {
        if (!active || e.defaultPrevented) return;
        var a = e.target.closest && e.target.closest('a[href]');
        var li = a && a.closest('ul.product-grid > li');
        if (li) active.save(li);
      }, true);
    }

    window.addEventListener('pagehide', function () { if (active) active.save(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
